import { signIn, signOut, resetPassword, confirmResetPassword, signUp, setUpTOTP, verifyTOTPSetup, updateMFAPreference, confirmSignIn } from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/api';
import { adminAddCognitoUserToAdminGroup, adminCreateCognitoUser, adminDeleteCognitoUser, adminRemoveCognitoUserFromAdminGroup, adminSetCognitoUserPassword, adminSetUserMFA, createUser, deleteUser, updateUser, handleUserDisplayOrder, createLoginHistory } from 'graphql/mutations';
import { listUsers, getUserByEmail, getUser, listLoginHistories } from 'graphql/queries';
import { AUTH_MODE } from 'constants/config';
import { convertDateTimeFormat, convertMomentFormat } from 'utils/function';
import { AccountType } from 'models';
import { apiDeleteUserDeviceGroups } from './UserDeviceGroups';
import { v4 as uuidv4 } from 'uuid';
const client = generateClient();

// Helper function to get last login time - returns LoginHistory table value, falls back to User table
const _getLastLoginTimeFromHistory = async (userId, userTableLastTimeLogin = null) => {
  try {
    let loginHistory = [];
    let nextToken = null;

    do {
      const response = await client.graphql({
        query: listLoginHistories,
        variables: {
          filter: {
            userId: { eq: userId },
            success: { eq: true }
          },
          limit: 300,
          nextToken
        },
        authMode: AUTH_MODE
      });

      const items = response.data?.listLoginHistories?.items?.filter(item => item !== null) || [];
      loginHistory = loginHistory.concat(items);
      nextToken = response.data?.listLoginHistories?.nextToken;
    } while (nextToken);

    let loginHistoryLastTime = null;
    if (loginHistory.length > 0) {
      // Sort by loginTime descending to get the most recent login
      loginHistory.sort((a, b) => new Date(b.loginTime).getTime() - new Date(a.loginTime).getTime());
      loginHistoryLastTime = new Date(loginHistory[0].loginTime).getTime();
    }

    return loginHistoryLastTime || userTableLastTimeLogin;
  } catch (error) {
    return userTableLastTimeLogin;
  }
};

export const loginCognitoUser = async (email, password) => {
  try {
    const res = await signIn({ username: email.trim(), password });
    return {
      success: true,
      data: res
    };
  } catch (e) {
    return { success: false, error: e }
  }
}

export async function signInConfirmation(totpCode) {
  try {
    const res = await confirmSignIn({ challengeResponse: totpCode });
    return {
      success: true,
      data: res
    };
  } catch (error) {
    return {
      success: false,
      error: error
    }
  }
}

export async function handleTOTPSetup(appName, accountName) {
  try {
    const totpSetupDetails = await setUpTOTP();
    const setupUri = totpSetupDetails.getSetupUri(appName, accountName);
    // Open setupUri with an authenticator APP to retrieve an OTP code
    return {
      success: true,
      data: setupUri.toString()
    }
  } catch (error) {
    console.log(error);
    return {
      success: false,
      error: error
    }
  }
}

export async function handleTOTPSetupVerification(totpCode) {
  try {
    await verifyTOTPSetup({ code: totpCode });
    return { success: true };
  } catch (error) {
    console.log(error);
    return { success: false, error: error };
  }
}

export async function handleUpdateMFAPreference() {
  try {
    await updateMFAPreference({ totp: 'PREFERRED' });
  } catch (error) {
    console.log(error);
  }
}

export const logoutCognitoUser = async () => {
  try {
    await signOut()
  } catch (e) {
    console.error(e);
  }
}

export const sendVerificationCodeForgotPassword = async (username) => {
  return await resetPassword({ username })
    .then((data) => { return { status: 1, message: data } })
    .catch((err) => { return { status: 0, message: err } });
}

export const submitNewPasswordWithVerificationCode = async (username, code, newPassword) => {
  return await confirmResetPassword({ username, confirmationCode: code, newPassword })
    .then((data) => { return { status: 1, message: data } })
    .catch((err) => { return { status: 0, message: err } });
}

const _apiAdminCreateCognitoUser = async (input) => {
  try {
    const response = await client.graphql({
      query: adminCreateCognitoUser,
      variables: {
        input
      },
      authMode: AUTH_MODE
    });
    return response.data?.adminCreateCognitoUser;
  } catch (e) {
    return {
      success: false,
      error: e
    };
  }
}

export const createCognitoUser = async (data) => {
  try {
    const response = await signUp({
      username: data.email,
      password: data.password,
      attributes: {
        email: data.email,
        name: data.name
      },
      autoSignIn: {
        enabled: false
      }
    })
    return response;
  } catch (e) {
    // console.error("ERROR", e);
  }
}

export const apiListUsers = async (nextToken = null) => {
  try {
    const response = await client.graphql({
      query: listUsers,
      variables: {
        nextToken
      },
      authMode: AUTH_MODE
    });
    const users = response.data?.listUsers?.items;
    for (const user of users) {
      user.createdAt = user?.createdAt && convertDateTimeFormat(user.createdAt);
      user.updatedAt = user?.updatedAt && convertDateTimeFormat(user.updatedAt);
      if (user.data) {
        // Tolerate malformed/double-encoded data on any single record so one bad
        // row can't throw and break the whole list. Always end up with an object.
        try {
          const parsed = JSON.parse(user.data);
          user.data = parsed && typeof parsed === 'object' ? parsed : {};
        } catch {
          user.data = {};
        }
      }
    }

    // Fetch last login time from LoginHistory for all users in parallel
    await Promise.all(users.map(async (user) => {
      const userLastTimeLogin = user.data?.lastTimeLogin || null;
      const lastLoginFromHistory = await _getLastLoginTimeFromHistory(user.id, userLastTimeLogin);
      if (!user.data) {
        user.data = {};
      }
      user.data.lastTimeLogin = lastLoginFromHistory;
    }));

    return {
      success: true,
      data: users
    };
  } catch (e) {
    return {
      success: false,
      error: e
    };
  }
}

export const apiGetUserByEmail = async (email) => {
  try {
    const response = await client.graphql({
      query: getUserByEmail,
      variables: {
        email
      },
      authMode: AUTH_MODE
    });
    const user = response.data?.getUserByEmail?.items[0];
    if (user?.createdAt) user.createdAt = convertDateTimeFormat(user.createdAt);
    if (user?.updatedAt) user.updatedAt = convertDateTimeFormat(user.updatedAt);
    if (user?.data) {
      user.data = JSON.parse(user.data);
    }
    return user;
  } catch (e) {
    console.log("🚀 ~ apiGetUserByEmail ~ e:", e);
    return null;
  }
}

export const apiCreateUser = async (input) => {
  try {
    // Prepare user data with display order
    let userData = {};
    if (input.data) {
      userData = typeof input.data === 'string' ? JSON.parse(input.data) : input.data;
    }

    // Assign the next available display order for new users
    if (!userData.displayOrder) {
      userData.displayOrder = await getNextDisplayOrder();
    }

    const createResult = await client.graphql({
      query: createUser,
      variables: {
        input: {
          id: uuidv4(),
          name: input.name,
          email: input.email,
          accountType: input.accountType,
          contact: input.contact,
          phone: input.phone,
          enabledMFA: input.enabledMFA,
          description: input.description,
          data: JSON.stringify(userData),
        }
      },
      authMode: AUTH_MODE
    });
    const createData = createResult.data?.createUser;
    const cognitoResult = await _apiAdminCreateCognitoUser({
      type: 'createUser',
      userId: createData.id,
      name: input.name,
      email: input.email,
      password: input.password,
    })
    if (cognitoResult.success) {
      if (input.accountType === AccountType.ADMIN || input.accountType === AccountType.MANAGER) {
        const changeRoleResult = await _addUserToAdminGroup(input.email);
        if (!changeRoleResult.success) return changeRoleResult;
      }
    }
    const updateResult = await apiUpdateUser({ id: createData.id, isActive: true });
    if (!updateResult.success) return updateResult;
    if (createData.updatedAt) createData.updatedAt = convertMomentFormat(createData.updatedAt);
    if (createData.createdAt) createData.createdAt = convertMomentFormat(createData.createdAt);
    if (createData.data) {
      createData.data = JSON.parse(createData.data);
    }
    return {
      success: true,
      data: createData
    };
  } catch (e) {
    console.log("🚀 ~ apiCreateUser ~ e:", e)
    return {
      success: false,
      error: e
    };
  }
}

const _addUserToAdminGroup = async (email) => {
  try {
    const response = await client.graphql({
      query: adminAddCognitoUserToAdminGroup,
      variables: {
        input: {
          type: 'addUserToAdminGroup',
          email
        }
      },
      authMode: AUTH_MODE
    });
    return response.data.adminAddCognitoUserToAdminGroup;
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const _removeUserFromAdminGroup = async (email) => {
  try {
    const response = await client.graphql({
      query: adminRemoveCognitoUserFromAdminGroup,
      variables: {
        input: {
          type: 'removeUserFromAdminGroup',
          email
        }
      },
      authMode: AUTH_MODE
    });
    return response.data.adminRemoveCognitoUserFromAdminGroup;
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const _setUserMFA = async (email, enabledMFA) => {
  try {
    const response = await client.graphql({
      query: adminSetUserMFA,
      variables: {
        input: {
          type: 'setUserMFA',
          email,
          enabledMFA
        }
      },
      authMode: AUTH_MODE
    });
    return response.data.adminSetUserMFA;
  } catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

export const apiUpdateUser = async (input) => {
  try {
    if (input.data) {
      input.data = JSON.stringify(input.data);
    }
    if (input.accountType) {
      const changeRoleResult = input.accountType === AccountType.USER ? await _removeUserFromAdminGroup(input.email) : await _addUserToAdminGroup(input.email);
      if (!changeRoleResult.success) {
        return changeRoleResult;
      }
    }
    if (input.enabledMFA !== undefined || input.enabledMFA !== null) {
      await _setUserMFA(input.email, input.enabledMFA);
    }

    const response = await client.graphql({
      query: updateUser,
      variables: {
        input
      },
      authMode: AUTH_MODE
    });
    const user = response.data?.updateUser;
    user.createdAt = user?.createdAt && convertDateTimeFormat(user.createdAt);
    user.updatedAt = user?.updatedAt && convertDateTimeFormat(user.updatedAt);
    if (user?.data) {
      user.data = JSON.parse(user.data);
    }
    return {
      success: true,
      data: user
    }
  } catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

export const apiUpdateDataUser = async(input) => {
  try {
    if (input.data) {
      input.data = JSON.stringify(input.data);
    }

    const response = await client.graphql({
      query: updateUser,
      variables: {
        input
      },
      authMode: AUTH_MODE
    });

    const user = response.data?.updateUser;

    return {
      success: true,
      data: user
    }
  } catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

// A missing Cognito user surfaces either as a handled payload error string
// (`{name=UserNotFoundException, ...}`) or as an Amplify v6 rejection carrying a
// GraphQL `errors` array. Detect both shapes.
const isUserNotFoundError = (err) => {
  try {
    return JSON.stringify(err).includes('UserNotFoundException')
      || String(err?.message || '').includes('UserNotFoundException');
  } catch {
    return String(err).includes('UserNotFoundException');
  }
};

export const apiDeleteUser = async (userId, userEmail) => {
  try {
    // A missing Cognito user (already deleted, or a create that half-failed) must
    // not block DynamoDB cleanup — otherwise the row is stuck as an orphan and every
    // retry shows an error. Only a *different* Cognito failure aborts the delete.
    try {
      const response = await client.graphql({
        query: adminDeleteCognitoUser,
        variables: {
          input: {
            type: 'deleteUser',
            email: userEmail
          }
        },
        authMode: AUTH_MODE
      });
      const adminDeleteResult = response.data.adminDeleteCognitoUser;
      if (!adminDeleteResult.success && !isUserNotFoundError(adminDeleteResult.error)) {
        return adminDeleteResult;
      }
    } catch (cognitoError) {
      if (!isUserNotFoundError(cognitoError)) throw cognitoError;
      // Cognito user already gone => proceed to remove the DynamoDB record.
    }

    await apiDeleteUserDeviceGroups(userId); // Delete user device groups if any

    let deletedUser = { id: userId };
    try {
      const deleteResult = await client.graphql({
        query: deleteUser,
        variables: {
          input: {
            id: userId
          }
        },
        authMode: AUTH_MODE
      });
      deletedUser = deleteResult.data.deleteUser;
    } catch (deleteError) {
      // Amplify v6 rejects when the response carries a GraphQL `errors` array even
      // though DynamoDB DeleteItem already committed. Verify the record is actually
      // gone before treating this as a real failure.
      const check = await client.graphql({
        query: getUser,
        variables: { id: userId },
        authMode: AUTH_MODE
      }).catch(() => null);
      if (check?.data?.getUser) throw deleteError; // still exists => real failure
      // record is gone => deletion succeeded despite the response error
    }

    // Compact display orders after successful deletion
    // This removes gaps in the display order sequence
    await compactDisplayOrders(userId);

    return {
      success: true,
      data: deletedUser
    };
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

export const apiGetUser = async (userId) => {
  try {
    const res = await client.graphql({
      query: getUser,
      variables: {
        id: userId
      },
      authMode: AUTH_MODE
    });
    const user = res.data?.getUser;
    if (user?.createdAt) user.createdAt = convertDateTimeFormat(user.createdAt);
    if (user?.udatedAt) user.udatedAt = convertDateTimeFormat(user.updatedAt);
    if (user?.data) {
      user.data = JSON.parse(user.data);
    }
    return {
      success: true,
      data: user
    };
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

export const apiAdminChangeUserPassword = async (userEmail, password) => {
  try {
    const response = await client.graphql({
      query: adminSetCognitoUserPassword,
      variables: {
        input: {
          type: 'setUserPassword',
          email: userEmail,
          password: password
        }
      },
      authMode: AUTH_MODE
    });
    return {
      success: true,
      data: response.data.adminSetCognitoUserPassword
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

export const apiUpdateUserLastTimeLogin = async (input) => {
  try {
    const response = await client.graphql({
      query: updateUser,
      variables: {
        input
      },
      authMode: AUTH_MODE
    });
    const user = response.data?.updateUser;
    user.createdAt = user?.createdAt && convertDateTimeFormat(user.createdAt);
    user.updatedAt = user?.updatedAt && convertDateTimeFormat(user.updatedAt);
    return {
      success: true,
      data: user,
      response: response
    }
  } catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

/**
 * Update display order for multiple users
 * @param {Array} userOrderData - Array of objects with {id, displayOrder}
 * @returns {Object} API response
 */
export const apiUpdateUsersDisplayOrder = async (userOrderData) => {
  try {
    // Use the Lambda function for batch processing
    const response = await client.graphql({
      query: handleUserDisplayOrder,
      variables: {
        input: {
          type: 'batchUpdateDisplayOrder',
          userOrderData: userOrderData
        }
      },
      authMode: AUTH_MODE
    });

    const result = response.data?.handleUserDisplayOrder;

    if (!result?.success) {
      return {
        success: false,
        error: result?.error || 'Unknown error',
        message: 'Failed to update users display order'
      };
    }

    // Format the response data
    const formattedUsers = result.updatedUsers?.map(user => {
      if (user) {
        user.createdAt = user?.createdAt && convertDateTimeFormat(user.createdAt);
        user.updatedAt = user?.updatedAt && convertDateTimeFormat(user.updatedAt);
        if (user?.data) {
          try {
            user.data = typeof user.data === 'string' ? JSON.parse(user.data) : user.data;
          } catch (e) {
            console.warn('Error parsing user data:', e);
          }
        }
      }
      return user;
    }) || [];

    return {
      success: true,
      data: formattedUsers,
      message: `Successfully updated display order for ${formattedUsers.length} users`
    };

  } catch (error) {
    console.error('Error updating users display order:', error);
    return {
      success: false,
      error: error,
      message: 'Failed to update users display order'
    };
  }
};

/**
 * Get users sorted by display order
 * @param {string} nextToken - Pagination token
 * @returns {Object} API response with sorted users
 */
export const apiListUsersSortedByDisplayOrder = async () => {
  try {
    const response = await apiListUsers();

    if (!response.success) {
      return response;
    }

    // Check if any users don't have displayOrder and assign them
    const usersNeedingOrder = response.data.filter(user =>
      !user.data?.displayOrder && user.data?.displayOrder !== 0
    );

    if (usersNeedingOrder.length > 0) {
      // Find the highest existing display order
      const maxOrder = response.data.reduce((max, user) => {
        const order = user.data?.displayOrder || 0;
        return Math.max(max, order);
      }, 0);

      // Assign display orders to users that don't have them
      const orderUpdates = usersNeedingOrder.map((user, index) => ({
        id: user.id,
        displayOrder: maxOrder + index + 1
      }));

      // Update these users with display orders
      await apiUpdateUsersDisplayOrder(orderUpdates);

      // Refetch the updated data
      const updatedResponse = await apiListUsers();
      if (updatedResponse.success) {
        response.data = updatedResponse.data;
      }
    }

    // Sort users by display order
    const sortedUsers = response.data.sort((a, b) => {
      const orderA = a.data?.displayOrder ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.data?.displayOrder ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });

    return {
      ...response,
      data: sortedUsers
    };

  } catch (error) {
    console.error('Error listing users sorted by display order:', error);
    return {
      success: false,
      error: error,
      message: 'Failed to list users sorted by display order'
    };
  }
};

/**
 * Get the next available display order for a new user
 * @returns {Promise<number>} Next display order number
 */
const getNextDisplayOrder = async () => {
  try {
    // Use the Lambda function for efficient next order calculation
    const response = await client.graphql({
      query: handleUserDisplayOrder,
      variables: {
        input: {
          type: 'getNextDisplayOrder'
        }
      },
      authMode: AUTH_MODE
    });

    const result = response.data?.handleUserDisplayOrder;

    if (result?.success) {
      const data = JSON.parse(result.data);
      return data.nextDisplayOrder;
    }

    return 1; // Default to 1 if Lambda function fails
  } catch (error) {
    console.error('Error getting next display order:', error);
    return 1;
  }
};

/**
 * Compact display orders after user deletion
 * This ensures there are no gaps in the display order sequence
 * @param {string} deletedUserId - ID of the deleted user
 * @returns {Promise<boolean>} Success status
 */
const compactDisplayOrders = async (deletedUserId) => {
  try {
    // Use the Lambda function for efficient compaction
    const response = await client.graphql({
      query: handleUserDisplayOrder,
      variables: {
        input: {
          type: 'compactDisplayOrders',
          deletedUserId: deletedUserId
        }
      },
      authMode: AUTH_MODE
    });

    const result = response.data?.handleUserDisplayOrder;

    if (!result?.success) {
      console.error('Failed to compact display orders:', result?.error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error compacting display orders:', error);
    return false;
  }
};

/**
 * Validate and fix display order consistency
 * This function ensures all users have proper sequential display orders
 * @returns {Promise<boolean>} Success status
 */
export const validateAndFixDisplayOrders = async () => {
  try {
    // Use the Lambda function for validation and fixing
    const response = await client.graphql({
      query: handleUserDisplayOrder,
      variables: {
        input: {
          type: 'validateAndFixDisplayOrders'
        }
      },
      authMode: AUTH_MODE
    });

    const result = response.data?.handleUserDisplayOrder;

    if (!result?.success) {
      console.error('Failed to validate and fix display orders:', result?.error);
      return false;
    }

    console.log('Display orders validated and fixed successfully');
    return true;
  } catch (error) {
    console.error('Error validating display orders:', error);
    return false;
  }
};

export const apiCreateLoginHistory = async (userId, success = true, ipAddress = null, userAgent = null) => {
  try {
    // Set TTL to 30 days from now to auto-expire old login history records
    const ttlTimestamp = Math.floor(Date.now() / 1000) + (30 * 24 * 60 * 60); // 30 days from now
    
    const response = await client.graphql({
      query: createLoginHistory,
      variables: {
        input: {
          userId,
          loginTime: new Date().toISOString(),
          success,
          ipAddress,
          userAgent,
          ttl: ttlTimestamp
        }
      },
      authMode: AUTH_MODE
    });
    return {
      success: true,
      data: response.data.createLoginHistory
    };
  } catch (error) {
    console.error('Failed to create login history:', error);
    // Don't fail the login process if history creation fails
    return {
      success: false,
      error: error
    };
  }
};

export const apiGetUserLoginHistory = async (userId) => {
  try {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    thirtyDaysAgo.setHours(0, 0, 0, 0);
    const thirtyDaysAgoISO = thirtyDaysAgo.toISOString();

    let loginHistory = [];
    let nextToken = null;

    do {
      const response = await client.graphql({
        query: listLoginHistories,
        variables: {
          filter: {
            userId: { eq: userId },
            loginTime: { ge: thirtyDaysAgoISO }
          },
          limit: 300,
          nextToken
        },
        authMode: AUTH_MODE
      });

      const items = response.data?.listLoginHistories?.items?.filter(item => item !== null) || [];
      loginHistory = loginHistory.concat(items);
      nextToken = response.data?.listLoginHistories?.nextToken;
    } while (nextToken);

    const formattedHistory = loginHistory.map(entry => ({
      timestamp: new Date(entry.loginTime).getTime(),
      success: entry.success
    }));
    
    return {
      success: true,
      data: formattedHistory
    };
  } catch (error) {
    return {
      success: false,
      error: error
    };
  }
};
